import { useState, useEffect } from 'react';
import { AppShell } from '../components/AppShell';
import { useUserOrg } from '../lib/org';
import {
  FEATURES,
  isFeatureEnabled,
  type FeatureKey,
} from '../lib/features';
import { updateOrgProfile } from '../lib/db';
import { LoadingIndicator } from '../components/LoadingIndicator';

/**
 * Settings → Features.
 *
 * Lists every gateable feature with a toggle. Flipping a toggle writes to
 * organisations.enabled_features / disabled_features so the change sticks
 * across sessions and propagates to other devices.
 */
export function SettingsFeatures() {
  const { org, refresh } = useUserOrg();
  const [busy, setBusy] = useState<FeatureKey | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2400);
    return () => clearTimeout(t);
  }, [toast]);

  async function toggle(key: FeatureKey, on: boolean) {
    if (!org) return;
    setBusy(key); setError(null);
    try {
      const enabled = new Set(org.enabled_features ?? []);
      const disabled = new Set(org.disabled_features ?? []);
      if (on) {
        enabled.add(key);
        disabled.delete(key);
      } else {
        disabled.add(key);
        enabled.delete(key);
      }
      await updateOrgProfile(org.org_id, {
        enabledFeatures: [...enabled],
        disabledFeatures: [...disabled],
      });
      await refresh();
      const meta = FEATURES.find((f) => f.key === key);
      setToast(on
        ? `${meta?.name ?? 'Feature'} turned on`
        : `${meta?.name ?? 'Feature'} turned off`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <AppShell title="Features" back="/settings">
      <p className="muted" style={{ margin: '4px 0 16px' }}>
        Turn parts of the app on or off. Hidden features don't appear in your menus
        until you turn them back on here.
      </p>

      {error && <div className="banner banner-error">{error}</div>}
      {toast && <div className="banner banner-success">{toast}</div>}

      {!org ? (
        <LoadingIndicator />
      ) : (
        <>
          {(['always','pc_only','optional'] as const).map((cat) => {
            const items = FEATURES.filter((f) => f.category === cat);
            if (items.length === 0) return null;
            const heading =
              cat === 'always' ? 'Core (always on)' :
              cat === 'pc_only' ? 'For when you run the job' :
              'Extras';
            return (
              <div key={cat} style={{ marginBottom: 18 }}>
                <h3 className="section-title" style={{ marginBottom: 8 }}>{heading}</h3>
                {items.map((f) => {
                  const on = isFeatureEnabled(org, f.key);
                  const lockedOn = f.category === 'always';
                  return (
                    <div key={f.key} className="feature-row">
                      <div className="feature-row-text">
                        <strong>{f.name}</strong>
                        <p className="muted" style={{ margin: '2px 0 0', fontSize: 13 }}>{f.description}</p>
                      </div>
                      <label className={`feature-toggle${on ? ' on' : ''}${lockedOn ? ' locked' : ''}`}>
                        <input type="checkbox"
                          checked={on}
                          disabled={lockedOn || busy === f.key}
                          onChange={(e) => toggle(f.key, e.target.checked)} />
                        <span className="feature-toggle-pip" />
                      </label>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </>
      )}
    </AppShell>
  );
}
